/**
 * Substitutes runtime placeholders in the SOW legal text with scope-specific
 * values. Called when rendering the legal text preview (and eventually
 * exports). The editable template always keeps the tokens so editing stays
 * clean.
 *
 * Tokens:
 *   [Client Name]  → scope.customerName (falls back to the literal token when
 *                    the customer name is empty)
 *   [ABN]          → " (ABN: {abn})" when customerAbn is non-empty, else ""
 *   [Insert Date]  → the issueDate when provided (formatted long-form), else
 *                    left as-is so the live editor preview keeps the token
 */

export interface LegalTextContext {
  customerName: string
  customerAbn: string
  /** Optional ISO date or Date instance. When set, replaces [Insert Date]. */
  issueDate?: string | Date
}

function formatIssueDate(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function substituteLegalTextPlaceholders(
  text: string,
  { customerName, customerAbn, issueDate }: LegalTextContext
): string {
  const name = (customerName || '').trim()
  const abn = (customerAbn || '').trim()

  let out = text
  out = out.replace(/\[Client Name\]/g, name || '[Client Name]')
  out = out.replace(/\[ABN\]/g, abn ? ` (ABN: ${abn})` : '')
  if (issueDate) {
    const formatted = formatIssueDate(issueDate)
    if (formatted) out = out.replace(/\[Insert Date\]/g, formatted)
  }
  // Collapse any double spaces introduced by an empty ABN replacement.
  out = out.replace(/ {2,}/g, ' ')
  return out
}
